import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';

// Sized so the icon's rendered height matches the wordmark's line height (text-base ->
// 24px line box) instead of the SVG's native 28x34 aspect ratio, which read visibly taller
// than the text next to it.
const LogoMark: React.FC = () => (
  <svg width="20" height="24" viewBox="0 0 28 34" fill="none" aria-hidden="true">
    <rect x="0" y="22" width="6" height="12" rx="1" fill="#0F2A44" />
    <rect x="10" y="16" width="6" height="18" rx="1" fill="#0F2A44" />
    <rect x="20" y="10" width="6" height="24" rx="1" fill="#0F2A44" />
    <path d="M2 20 Q14 6 26 9" stroke="#3BCC8F" strokeWidth="1.5" strokeLinecap="round" fill="none" />
    <path d="M24 5 L28 10 L22 12 Z" fill="#3BCC8F" />
  </svg>
);

const toolCategories = [
  {
    label: 'Personal Finance',
    items: [
      { label: 'Financial Goal Planner', to: '/financial-goal-planner', tag: 'Free' },
      { label: 'Budget Planner',         to: '/tools/budget',           tag: 'Free' },
      { label: 'Debt Payoff Calculator', to: '/tools/debt-payoff',      tag: 'Free' },
      { label: 'M-Pesa Analyser',        to: '/tools/mpesa',            tag: 'Free' },
      { label: 'Student Budget',         to: '/tools/student-budget',   tag: 'Free' },
    ],
  },
  {
    label: 'Business',
    items: [
      { label: 'P&L Calculator',      to: '/tools/bookkeeping', tag: 'Free' },
      { label: 'Invoice Generator',   to: '/tools/invoice',     tag: 'Free' },
      { label: 'Inventory Calculator', to: '/tools/inventory',  tag: 'Free' },
    ],
  },
  {
    label: 'Investing',
    items: [
      { label: 'Portfolio Tracker',     to: '/tools/portfolio',    tag: 'Free' },
      { label: 'Chama Calculator',      to: '/tools/chama',        tag: 'Free' },
      { label: 'Forex Calculator',      to: '/tools/forex',        tag: 'Free' },
      { label: 'Compound Calculator',   to: '/app/calculator',     tag: 'Free' },
    ],
  },
  {
    label: 'Property & Lifestyle',
    items: [
      { label: 'Rental Yield Calculator',   to: '/tools/rental',      tag: 'Free' },
      { label: 'Airbnb Revenue Calculator', to: '/tools/airbnb',      tag: 'Free' },
      { label: 'Weight Loss Tracker',       to: '/tools/weight-loss', tag: 'Free' },
    ],
  },
];

const company = [
  { label: 'About',   to: '/about'   },
  { label: 'FAQ',     to: '/faq'     },
  { label: 'Contact', to: '/contact' },
];

const CompanyHeader: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
        setToolsOpen(false);
        setCompanyOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const appTarget = isAuthenticated ? '/app/dashboard' : '/login';

  return (
    <header
      ref={menuRef}
      className="fixed top-0 left-0 right-0 z-50 bg-paper/95 dark:bg-ink-950/95 backdrop-blur-sm border-b border-ink-10 dark:border-white/5"
    >
      <div className="max-w-[1080px] mx-auto px-8 sm:px-6">
        <div className="flex justify-between items-center h-16">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity duration-150">
            <LogoMark />
            <span className="font-serif text-base text-ink dark:text-paper">FinInteract</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">

            <Link to="/shop"
              className="text-ink-60 dark:text-ink-20 hover:text-teal text-sm font-medium transition-colors duration-150">
              Shop
            </Link>

            {/* Tools dropdown */}
            <div className="relative">
              <button
                onClick={() => setToolsOpen(v => !v)}
                className="flex items-center gap-1 text-ink-60 dark:text-ink-20 hover:text-ink dark:hover:text-paper text-sm font-medium transition-colors duration-150"
              >
                Tools
                <svg className={`w-3.5 h-3.5 transition-transform duration-150 ${toolsOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <AnimatePresence>
                {toolsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 mt-2 w-[520px] bg-white dark:bg-ink border border-ink-10 dark:border-white/5 rounded-brand-lg shadow-sm overflow-hidden"
                  >
                    <div className="grid grid-cols-2 divide-x divide-ink-10 dark:divide-white/10">
                      {toolCategories.map(cat => (
                        <div key={cat.label} className="py-3">
                          <p className="px-4 pb-1.5 text-[9px] font-mono uppercase tracking-widest text-ink-40">{cat.label}</p>
                          {cat.items.map(t => (
                            <Link key={t.label} to={t.to}
                              onClick={() => setToolsOpen(false)}
                              className="flex items-center justify-between px-4 py-2 text-sm text-ink-60 dark:text-ink-20 hover:text-ink dark:hover:text-paper hover:bg-paper dark:hover:bg-white/5 transition-colors duration-150">
                              <span>{t.label}</span>
                              <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-mint/20 text-ink dark:text-paper">
                                {t.tag}
                              </span>
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Company dropdown */}
            <div className="relative">
              <button
                onClick={() => setCompanyOpen(v => !v)}
                className="flex items-center gap-1 text-ink-60 dark:text-ink-20 hover:text-ink dark:hover:text-paper text-sm font-medium transition-colors duration-150"
              >
                Company
                <svg className={`w-3.5 h-3.5 transition-transform duration-150 ${companyOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <AnimatePresence>
                {companyOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 mt-2 w-44 bg-white dark:bg-ink border border-ink-10 dark:border-white/5 rounded-brand-lg shadow-sm overflow-hidden"
                  >
                    {company.map(c => (
                      <Link key={c.to} to={c.to}
                        onClick={() => setCompanyOpen(false)}
                        className="block px-4 py-3 text-sm text-ink-60 dark:text-ink-20 hover:text-ink dark:hover:text-paper hover:bg-paper dark:hover:bg-white/5 transition-colors duration-150">
                        {c.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {!isAuthenticated && (
              <Link to="/login"
                className="text-ink-60 dark:text-ink-20 hover:text-ink dark:hover:text-paper text-sm font-medium transition-colors duration-150">
                Sign in
              </Link>
            )}

            <ThemeToggle
              className="p-2 rounded-brand text-ink-60 dark:text-ink-20 hover:text-ink dark:hover:text-paper hover:bg-ink-10 dark:hover:bg-white/5 transition-colors duration-150"
              iconClassName="w-4 h-4"
            />

            <Link to={appTarget}
              className="bg-ink text-white px-5 py-2 rounded-brand text-sm font-medium hover:bg-ink/90 transition-colors duration-150 flex items-center gap-2">
              {isAuthenticated ? <><LayoutDashboard className="w-4 h-4" />Dashboard</> : 'Get started'}
            </Link>
          </nav>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-brand hover:bg-ink-10 dark:hover:bg-white/5 transition-colors duration-150"
            onClick={() => setIsMenuOpen(v => !v)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-5 h-5 text-ink-60 dark:text-ink-20" /> : <Menu className="w-5 h-5 text-ink-60 dark:text-ink-20" />}
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="md:hidden overflow-hidden"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="py-4 border-t border-ink-10 dark:border-white/5 space-y-1">
                <Link to="/shop" onClick={() => setIsMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-teal transition-colors duration-150">
                  Shop
                </Link>
                {toolCategories.map(cat => (
                  <div key={cat.label}>
                    <p className="pt-3 pb-1 text-xs font-mono uppercase tracking-wide text-ink-40">{cat.label}</p>
                    {cat.items.map(t => (
                      <Link key={t.label} to={t.to}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center justify-between py-2 text-sm text-ink-60 dark:text-ink-20 hover:text-ink dark:hover:text-paper font-medium transition-colors duration-150">
                        <span>{t.label}</span>
                        <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-mint/20 text-ink dark:text-paper">Free</span>
                      </Link>
                    ))}
                  </div>
                ))}
                <p className="pt-3 pb-1 text-xs font-mono uppercase tracking-wide text-ink-40">Company</p>
                {company.map(c => (
                  <Link key={c.to} to={c.to}
                    onClick={() => setIsMenuOpen(false)}
                    className="block py-2 text-sm text-ink-60 dark:text-ink-20 hover:text-ink dark:hover:text-paper font-medium transition-colors duration-150">
                    {c.label}
                  </Link>
                ))}
                <ThemeToggle
                  showLabel
                  onToggled={() => setIsMenuOpen(false)}
                  className="flex items-center gap-2.5 w-full py-2 text-sm text-ink-60 dark:text-ink-20 hover:text-ink dark:hover:text-paper font-medium transition-colors duration-150"
                  iconClassName="w-4 h-4 flex-shrink-0"
                />
                <div className="pt-2 flex flex-col gap-2">
                  {!isAuthenticated && (
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}
                      className="border border-ink dark:border-white/30 text-ink dark:text-paper px-5 py-2.5 rounded-brand text-sm font-medium text-center hover:bg-ink hover:text-white dark:hover:bg-paper dark:hover:text-ink transition-all duration-150">
                      Sign in
                    </Link>
                  )}
                  <Link to={appTarget} onClick={() => setIsMenuOpen(false)}
                    className="bg-ink text-white px-5 py-2.5 rounded-brand text-sm font-medium text-center hover:bg-ink/90 transition-colors duration-150 flex items-center justify-center gap-2">
                    {isAuthenticated ? <><LayoutDashboard className="w-4 h-4" />Dashboard</> : 'Get started'}
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default CompanyHeader;
